import { settleToTarget, disableScrollRestoration } from '../utils/scroll';

const TAB_SCROLL_OFFSET_PX = 32;
const BASE_TAB_PREFIXES = ['faq-tabs', 'program-schedule-tabs'];
const BASE_TAB_SELECTOR = BASE_TAB_PREFIXES.map(prefix => `[data-component="${prefix}"]`).join(', ');

const textToSlug = (text) => text.trim().toLowerCase().replace(/\s+/g, '-');

const findBaseTabMatches = (slug) =>
  BASE_TAB_PREFIXES.flatMap(prefix =>
    Array.from(document.querySelectorAll(`[data-component="${prefix}"]`))
      .map(element => {
        const navItems = Array.from(element.querySelectorAll(`.${prefix}__nav-item`));
        const navItem = slug
          ? navItems.find(item => {
            const span = item.querySelector('span');
            return span && textToSlug(span.textContent) === slug;
          })
          : navItems[0];
        return { element, navItem };
      })
      .filter(({ navItem }) => navItem)
  );

export const isBaseTabUrl = (href = window.location.href) => {
  const slug = new URL(href, window.location.href).searchParams.get('tab');
  return Boolean(slug) && findBaseTabMatches(slug).length > 0;
};

class TabLink {
  constructor() {
    this.init();
  }

  updateUrl(slug) {
    const url = new URL(window.location);
    if (url.searchParams.get('tab') === slug) return;
    url.searchParams.set('tab', slug);
    window.history.pushState({}, '', url);
  }

  getStickyHeaderHeight() {
    return document.querySelector('.header-main')?.closest('.wp-block-template-part')?.offsetHeight ?? 0;
  }

  getCurrentTab() {
    return new URLSearchParams(window.location.search).get('tab');
  }

  init() {
    this.initJetEngineTabs();

    if (!document.querySelector(BASE_TAB_SELECTOR)) return;

    BASE_TAB_PREFIXES.forEach(prefix => this.initBaseTabs(prefix));
    this.restoreBaseTabs();
    this.initInPageTabLinks();
    window.addEventListener('popstate', () => this.activateBaseTabs(this.getCurrentTab()));
  }

  initJetEngineTabs() {
    if (!document.querySelector('.tabs__link')) return;

    document.body.addEventListener('click', (e) => {
      const listItem = e.target.closest('.tabs_list_container > *');
      if (!listItem) return;
      this.handleJetEngineClick(listItem);
    });

    const currentTab = this.getCurrentTab();
    if (currentTab) {
      this.restoreJetEngineTab(currentTab);
    }
  }

  handleJetEngineClick(listItem) {
    if (!listItem) return;
    const p = listItem.querySelector('p');
    if (!p) return;
    const slug = textToSlug(p.textContent);
    this.updateUrl(slug);
  }

  restoreJetEngineTab(currentTab) {
    const observer = new MutationObserver(() => {
      const allItems = document.querySelectorAll('.tabs_list_container > *');
      allItems.forEach(item => {
        const p = item.querySelector('p');
        if (!p) return;
        const slug = textToSlug(p.textContent);
        if (slug === currentTab) {
          setTimeout(() => {
            item.click();
          }, 210);
          observer.disconnect();
        }
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  initBaseTabs(prefix) {
    const elements = Array.from(document.querySelectorAll(`[data-component="${prefix}"]`));

    elements.forEach(element => {
      element.querySelectorAll(`.${prefix}__nav-item, .${prefix}__select-option`).forEach(item => {
        item.addEventListener('click', () => {
          if (this.isSyncingFromUrl) return;
          const span = item.querySelector('span');
          if (span) {
            this.updateUrl(textToSlug(span.textContent));
          }
        });
      });
    });
  }

  activateBaseTabs(slug) {
    const matches = findBaseTabMatches(slug);
    this.isSyncingFromUrl = true;
    matches.forEach(({ navItem }) => navItem.click());
    this.isSyncingFromUrl = false;
    return matches;
  }

  getFirstElement(matches) {
    const [first] = matches.map(({ element }) => element).sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
    return first;
  }

  getVisibleHashTarget(hash) {
    if (!hash || hash === '#') return null;
    let target = null;
    try {
      target = document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
      return null;
    }
    return target?.getClientRects().length ? target : null;
  }

  scrollToTabs(matches, hash) {
    const fallback = this.getFirstElement(matches);
    settleToTarget({
      getTarget: () => this.getVisibleHashTarget(hash) ?? fallback,
      offset: this.getStickyHeaderHeight() + TAB_SCROLL_OFFSET_PX,
    });
  }

  isSameWindowLink(link) {
    if (link.hasAttribute('download')) return false;
    const target = link.target.toLowerCase();
    if (!target || target === '_self') return true;
    if (target === '_parent') return window.parent === window;
    if (target === '_top') return window.top === window;
    return false;
  }

  differsOnlyByTab(url) {
    const current = new URL(window.location.href);
    if (url.origin !== current.origin || url.pathname !== current.pathname) return false;

    const withoutTab = (params) => {
      const copy = new URLSearchParams(params);
      copy.delete('tab');
      copy.sort();
      return copy.toString();
    };
    return withoutTab(url.searchParams) === withoutTab(current.searchParams);
  }

  initInPageTabLinks() {
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const link = e.target.closest('a[href]');
      if (!link || !this.isSameWindowLink(link)) return;

      const url = new URL(link.href, window.location.href);
      const slug = url.searchParams.get('tab');
      if (!slug || !this.differsOnlyByTab(url)) return;
      if (findBaseTabMatches(slug).length === 0) return;

      e.preventDefault();
      if (url.href !== window.location.href) {
        window.history.pushState({}, '', url);
      }
      this.scrollToTabs(this.activateBaseTabs(slug), url.hash);
    });
  }

  restoreBaseTabs() {
    const slug = this.getCurrentTab();
    if (!slug || findBaseTabMatches(slug).length === 0) return;

    disableScrollRestoration();
    setTimeout(() => {
      this.scrollToTabs(this.activateBaseTabs(slug), window.location.hash);
    }, 100);
  }

  static init() {
    new TabLink();
  }
}

export default TabLink;
